import {
  CruisePurchaseLiteModel,
  CruisePurchaseModel,
  getCruisePurchaseKey,
  getCruisePurchasesKey,
  useGetCruisePurchase,
  useGetCruisePurchases,
} from 'api-hooks/order/cruise';
import { OnChangeParams, Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { PurchaseStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: CruisePurchaseLiteModel | CruisePurchaseModel,
): Option {
  const agentName =
    typeof item.agent === 'object' ? item.agent.name : item.agent;

  return {
    id: item.id,
    label: transformLabel([item.number, item.code, agentName]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: CruisePurchaseModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function CruisePurchaseSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:cruise_purchase'),
    placeholder = t('common:select_cruise_purchase'),
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetCruisePurchases,
    useDetailLazyQueryHook: useGetCruisePurchase,
    listKeyFunction: getCruisePurchasesKey,
    detailKeyFunction: getCruisePurchaseKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: { status: PurchaseStatusEnum.Pending },
        },
      }),
      [],
    ),
    renderCreate: !!can(AuthorizationRules.CruisePurchasesCreate),
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.CruisePurchasesCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, CruisePurchaseModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      overrides={overrides}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
